import React from 'react';
import { ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ImageVersion } from './types';
import { formatVersionLabel, formatVersionDropdown, sortVersionsByDate } from '@/utils/dateUtils';

interface VersionSelectorProps {
  versions: ImageVersion[];
  selectedVersion: string;
  onVersionChange: (version: string) => void;
  className?: string;
}

export const VersionSelector: React.FC<VersionSelectorProps> = ({
  versions,
  selectedVersion,
  onVersionChange,
  className = ''
}) => {
  const sortedVersions = sortVersionsByDate(versions);
  const currentVersion = versions.find(v => v.version === selectedVersion) || versions[0];

  if (versions.length <= 1) {
    return (
      <div className={`px-2 py-1 bg-muted rounded-md text-xs text-muted-foreground ${className}`}>
        {formatVersionLabel(currentVersion?.version || '')}
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          className={`h-6 px-2 text-xs bg-background/80 backdrop-blur-sm border-border/50 hover:bg-background/90 ${className}`}
        >
          {formatVersionLabel(currentVersion?.version || '')}
          <ChevronDown className="h-3 w-3 ml-1" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="center" 
        className="min-w-[140px] bg-background/95 backdrop-blur-sm border-border/50"
      >
        {sortedVersions.map((version) => (
          <DropdownMenuItem
            key={version.version}
            onClick={() => onVersionChange(version.version)}
            className={`text-xs cursor-pointer ${
              version.version === selectedVersion 
                ? 'bg-primary/10 text-primary' 
                : 'hover:bg-muted/50'
            }`}
          >
            {formatVersionDropdown(version.version)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};